// #region IMPORTS
import React from 'react';
import { SectionTitle } from './SectionTitle';
import { CardList } from '../Cards/CardList';
import { Card } from '../Cards/Card';
import azchestImage from '../Assets/images/azchest.png';
import aztraumaImage from '../Assets/images/aztrauma.png';
import azmeasureImage from '../Assets/images/azmeasure.png';
import azboneageImage from '../Assets/images/azboneage.png';
import noduleImage from '../Assets/images/nodule.png';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectRegion } from '../Components/Filter/FiltersSlice';
// CSS
import './VerticalCardsContainer.css';
// #endregion

// #region CONSTANTS
export const ALGORITHMS = {
  AZCHEST: 'AZCHEST',
  AZTRAUMA: 'AZTRAUMA',
  AZMEASURE: 'AZMEASURE',
  AZBONEAGE: 'AZBONEAGE',
  AZNODE: 'AZNOD',
};

const ITEMS = [
  {
    id: 2,
    name: ALGORITHMS.AZTRAUMA,
    libelle: 'trauma',
    description:
      'Detecting fractures, dislocations, and joint effusions on X-rays',
    image: aztraumaImage,
    algo: ALGORITHMS.AZTRAUMA,
    certifs: ['CE', 'FDA'],
    otherCertifs: ['HSA', 'ANVISA', 'MDA', 'SFDA', 'TGA'],
  },
  {
    id: 1,
    name: ALGORITHMS.AZCHEST,
    libelle: 'chest',
    description:
      'Categorizes, detects and reports the main cardiac and lung abnormalities on X-rays',
    image: azchestImage,
    algo: ALGORITHMS.AZCHEST,
    certifs: ['CE', 'FDA'],
    otherCertifs: ['HSA', 'ANVISA', 'MDA', 'SFDA'],
  },
  {
    id: 3,
    name: ALGORITHMS.AZMEASURE,
    libelle: 'measure',
    description:
      'Automated characterization of osteo-articular geometries, including lengths and angular positions',
    image: azmeasureImage,
    algo: ALGORITHMS.AZMEASURE,
    certifs: ['CE'],
    excludeRegions: ['Brazil', 'USA'],
    otherCertifs: ['HSA', 'MDA'],
  },
  {
    id: 4,
    name: ALGORITHMS.AZBONEAGE,
    libelle: 'boneage',
    description:
      'Provides advanced automated calculation of the bone age of pediatric patients, based on the reference Greulich & Pyle methodology',
    image: azboneageImage,
    algo: ALGORITHMS.AZBONEAGE,
    certifs: ['CE'],
    excludeRegions: ['Brazil'],
    otherCertifs: [],
  },
];

const NODE_ITEMS = [
  {
    id: 1,
    name: ALGORITHMS.AZNODE,
    libelle: 'nod',
    description:
      'Detection and characterization of pulmonary nodules on chest CT scans',
    image: noduleImage,
    algo: ALGORITHMS.AZNODE,
    certifs: ['CE'],
    otherCertifs: [],
  },
];

export const AZNODE_BACKGROUND =
  'linear-gradient(to right, #ff4242ff ,#ff4242ff 1%, #ff424265 1%, #00000000 90%)';
// #endregion

//#region COMPONENT
/**
 * @function VerticalCardsContainer
 * @param {{verticals: array, onClick: function, children: React.ReactNode}} props
 * @returns {JSX.Element} The VerticalCardsContainer component
 */
const VerticalCardsContainer = props => {
  // Properties
  const { verticals = [], onClick, children } = props;

  // Selectors
  const region = useSelector(selectRegion);

  // Constants
  const RAYVOLVE_ITEMS = useMemo(() => {
    if (!verticals || !verticals.length || !region) return [];
    // return ITEMS;
    return ITEMS.filter(item =>
      verticals.some(
        vertical =>
          item.algo.toUpperCase() === vertical.name.toUpperCase() &&
          !(
            item.excludeRegions &&
            region &&
            item.excludeRegions.some(excludeRegion => excludeRegion === region)
          )
      )
    );
  }, [verticals, region]);

  const RAYSCAN_ITEMS = useMemo(() => {
    // return NODE_ITEMS;
    if (!verticals || !verticals.length) return [];
    return NODE_ITEMS.filter(item =>
      verticals.some(
        vertical => item.name.toUpperCase() === vertical.name.toUpperCase()
      )
    );
  }, [verticals]);

  const isItems = useMemo(() => {
    return (
      (RAYSCAN_ITEMS && RAYSCAN_ITEMS.length > 0) ||
      (RAYVOLVE_ITEMS && RAYVOLVE_ITEMS.length > 0)
    );
  }, [RAYSCAN_ITEMS, RAYVOLVE_ITEMS]);

  // Interface
  if (!isItems) {
    return (
      <div className="VerticalCardContainer-NoItem">
        <SectionTitle>No product available</SectionTitle>
      </div>
    );
  }

  return (
    <>
      <div className="VerticalCardContainer-Container">
        {RAYVOLVE_ITEMS && RAYVOLVE_ITEMS.length && (
          <div className="VerticalCardContainer-Section">
            <div className="VerticalCardContainer-SectionTitle">
              <SectionTitle>Standard X-rays</SectionTitle>
            </div>
            {children}
            <CardList>
              {RAYVOLVE_ITEMS.map((item, index) => {
                return (
                  <div key={item.id}>
                    <Card
                      image={item.image}
                      index={index}
                      description={
                        <>
                          <div className="VerticalCardContainer-DescriptionCertifsContainer">
                            <div className="VerticalCardContainer-CardDescription">
                              {item.description}
                            </div>
                            <div className="VerticalCardContainer-CertifsContainer">
                              <div style={{ width: '100%' }}>
                                {item.certifs.map(certif => (
                                  <div
                                    className="VerticalCardContainer-CertifCard"
                                    key={certif}
                                  >
                                    {certif}
                                  </div>
                                ))}
                              </div>
                              <div style={{ marginTop: 5 }}>
                                {item.otherCertifs.map(certif => (
                                  <div
                                    className="VerticalCardContainer-CertifCard"
                                    key={certif}
                                  >
                                    {certif}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      }
                      libelle={item.libelle}
                      onClick={() => onClick(item)}
                    />
                  </div>
                );
              })}
            </CardList>
          </div>
        )}
        {RAYSCAN_ITEMS && RAYSCAN_ITEMS.length && (
          <div className="VerticalCardContainer-Section">
            <div style={{ display: 'flex' }}>
              <SectionTitle background={AZNODE_BACKGROUND}>
                CT-Scan
              </SectionTitle>
            </div>
            <CardList>
              {RAYSCAN_ITEMS.map((item, index) => {
                return (
                  <div key={item.id}>
                    <Card
                      hoverBoxShadowColor="#ff4242ff"
                      image={item.image}
                      description={
                        <div className="VerticalCardContainer-DescriptionCertifsContainer">
                          <div className="VerticalCardContainer-CardDescription">
                            {item.description}
                          </div>
                          <div className="VerticalCardContainer-CertifsContainer">
                            <div style={{ width: '100%' }}>
                              {item.certifs.map(certif => (
                                <div
                                  className="VerticalCardContainer-CertifCard"
                                  key={certif}
                                >
                                  {certif}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      }
                      libelle={item.libelle}
                      onClick={() => onClick(item)}
                    />
                  </div>
                );
              })}
            </CardList>
          </div>
        )}
      </div>
    </>
  );
};
//#endregion

//#region PROPERTIES
VerticalCardsContainer.propTypes = {
  onClick: PropTypes.func.isRequired,
  verticals: PropTypes.array.isRequired,
  children: PropTypes.node,
};
//#endregion

export { VerticalCardsContainer };
